// safety/incidents-and-injuries/components/NewIncidentPage.tsx
import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { IncidentForm } from './IncidentForm';
import FileAttachments from '../../../../components/FileAttachments';
import type { Attachment } from '../../../../types/index';
import type { Incident, SafetyLists, EmployeeRef, EntityRef, IncidentAttachmentsByCategory } from '../../../../types/incident.types';

const emptyIncident = (): Partial<Incident> => ({
    status: 'Open',
    employeeId: '',
    employeeName: '',
    entityId: '',
    entityName: '',
    project: '',
    supervisor: '',
    date: '',
    time: '',
    witnesses: '',
    cause: '',
    bodyPart: '',
    nature: '',
    description: '',
    firstAidApplied: true,
    firstAiderName: '',
    firstAidTreatment: '',
    stayedAtWork: null,
    hospitalised: null,
    fitToReturn: null,
    capacity: '',
    rtwCoordinator: '',
    nextAppt: '',
    rtwPlan: {
        pendingNote: '',
        restrictions: { lifting: '', carrying: '', pushing: '', standing: '', sitting: '', driving: '', other: '' },
        hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
        approvals: [],
    },
    workflow: [],
    certificates: [],
    appointments: [],
    expenses: [],
    wages: [],
    notes: [],
    photos: [],
    incidentReports: [],
    medicalDocuments: [],
});

export const NewIncidentPage: React.FC<{
    lists: SafetyLists;
    employees: EmployeeRef[];
    entities: EntityRef[];
    onCancel: () => void;
    onCreate: (incident: Partial<Incident>, attachments: IncidentAttachmentsByCategory) => void | Promise<void>;
}> = ({ lists, employees, entities, onCancel, onCreate }) => {
    const [d, setD] = useState<Partial<Incident>>(() => emptyIncident());
    const set = (patch: Partial<Incident>) => setD((prev) => ({ ...prev, ...patch }));

    // Staged attachment files (uploaded to S3 after the incident is created)
    const [photos, setPhotos] = useState<Attachment[]>([]);
    const [incidentReports, setIncidentReports] = useState<Attachment[]>([]);
    const [medicalDocuments, setMedicalDocuments] = useState<Attachment[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!d.employeeName) {
            toast.error('Employee name is required');
            return;
        }
        if (!d.date) {
            toast.error('Incident date is required');
            return;
        }
        setSubmitting(true);
        try {
            await onCreate(
                {
                    ...d,
                    capacity: d.fitToReturn ? 'Light Duties' : 'No Work',
                    workflow: [
                        { step: 1, label: 'Injury Report Completed', who: d.employeeName!, done: true, date: d.date, auto: true },
                    ],
                },
                { photos, incidentReports, medicalDocuments },
            );
        } finally {
            setSubmitting(false);
        }
    };

    const SubmitButton = (
        <button
            onClick={submit}
            disabled={submitting}
            className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {submitting ? 'Submitting…' : 'Submit Incident'}
        </button>
    );

    return (
        <div className="bg-background min-h-screen pb-10 px-0 sm:px-4 lg:px-0">
            <div className="max-w-3xl mx-auto px-0 sm:px-4 lg:px-0 py-7">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
                            <ChevronLeft size={16} />
                            Incident Register
                        </button>
                        <h1 className="text-2xl font-medium">New Incident Report</h1>
                        <p className="text-sm text-muted-foreground mt-1">Complete all sections to log a workplace incident.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                            Cancel
                        </button>
                        {SubmitButton}
                    </div>
                </div>

                <IncidentForm value={d} onChange={set} lists={lists} employees={employees} entities={entities} />

                <div className="mb-6">
                    <FileAttachments
                        title="Photos"
                        resourceType="safety-incidents"
                        category="photos"
                        attachments={photos}
                        onAttachmentsChange={setPhotos}
                    />
                </div>

                <div className="mb-6">
                    <FileAttachments
                        title="Incident Reports"
                        resourceType="safety-incidents"
                        category="incidentReports"
                        attachments={incidentReports}
                        onAttachmentsChange={setIncidentReports}
                    />
                </div>

                <div className="mb-6">
                    <FileAttachments
                        title="Medical Documents"
                        resourceType="safety-incidents"
                        category="medicalDocuments"
                        attachments={medicalDocuments}
                        onAttachmentsChange={setMedicalDocuments}
                    />
                </div>

                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                        Cancel
                    </button>
                    {SubmitButton}
                </div>
            </div>
        </div>
    );
};

export default NewIncidentPage;
